import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { approvalsApi, ApprovalKind } from '@/lib/approvals';
import { workspaceExtras } from '@/lib/workspace-extras';
import { useDialogs } from '@/components/app/DialogProvider';
import { Modal, InlineAlert } from '@/components/app/Modal';

/**
 * "Request sign-off" — connects an AI result to the Approvals flow that the app
 * advertises everywhere but never wired (approvalsApi.create was unused). Pick
 * the required signers, in order, and a pending approval is created.
 */
export function RequestSignoffButton({
  wsId,
  targetKind,
  targetId,
}: {
  wsId: string;
  targetKind: ApprovalKind;
  targetId: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { alert } = useDialogs();
  const [open, setOpen] = useState(false);
  const [signers, setSigners] = useState<string[]>([]);

  const { data: people = [] } = useQuery({
    queryKey: ['people', wsId],
    queryFn: () => workspaceExtras.people(wsId),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: () => approvalsApi.create(wsId, { targetKind, targetId, requiredSigners: signers }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approvals', wsId] });
      qc.invalidateQueries({ queryKey: ['next-actions', wsId] });
      setOpen(false);
      setSigners([]);
      void alert({ title: t('signoff.requested', { defaultValue: 'Sign-off requested.' }) as string });
    },
    onError: () => alert({ title: t('signoff.error', { defaultValue: 'Could not request sign-off.' }) as string }),
  });

  const toggle = (id: string) =>
    setSigners((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  return (
    <>
      <button onClick={() => setOpen(true)} className="text-[12px] text-accent-600 hover:underline">
        {t('signoff.request', { defaultValue: 'Request sign-off' })}
      </button>

      <Modal
        open={open}
        onOpenChange={(n) => { setOpen(n); if (!n) { setSigners([]); create.reset(); } }}
        title={t('signoff.title', { defaultValue: 'Request sign-off' }) as string}
        description={t('signoff.body', { defaultValue: 'Choose who must approve this, in order.' }) as string}
      >
        {create.isError && <InlineAlert tone="error">{t('signoff.error', { defaultValue: 'Could not request sign-off.' })}</InlineAlert>}
        <ul className="mt-1 max-h-64 overflow-y-auto space-y-1">
          {people.map((p) => (
            <li key={p.userId}>
              <label className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-ink-50 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={signers.includes(p.userId)}
                  onChange={() => toggle(p.userId)}
                />
                <span className="text-ink-900">{p.name || p.email || p.userId}</span>
                <span className="text-[11px] text-ink-400 ml-auto uppercase">{p.role}</span>
                {signers.includes(p.userId) && (
                  <span className="text-[11px] text-accent-600 font-semibold">#{signers.indexOf(p.userId) + 1}</span>
                )}
              </label>
            </li>
          ))}
        </ul>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={() => setOpen(false)} className="btn-ghost px-3.5 py-2 text-sm">
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            onClick={() => create.mutate()}
            disabled={create.isPending || signers.length === 0}
            className="btn-press px-3.5 py-2 text-sm"
          >
            {t('signoff.submit', { defaultValue: 'Request sign-off' })}
          </button>
        </div>
      </Modal>
    </>
  );
}
